import { BarChart3, CreditCard, FileWarning, Gift, FileText, Lightbulb, TrendingUp, Scale } from 'lucide-react';

import type { SalesAnalysisReport } from '../types/report';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { KPICard } from './charts/KPICard';
import { DiscountEfficiencyChart } from './charts/DiscountEfficiencyChart';
import { PaymentPieChart } from './charts/PaymentPieChart';
import { UnpaidBarChart } from './charts/UnpaidBarChart';
import { ServiceMetricsChart } from './charts/ServiceMetricsChart';
import { ContractChart } from './charts/ContractChart';
import { BranchSalesChart } from './charts/BranchSalesChart';
import { StrategyCard } from './StrategyCard';
import { MarketComparisonChart } from './charts/MarketComparisonChart';
import { UnpaidCalculationChart } from './charts/UnpaidCalculationChart';

interface DashboardProps {
  report: SalesAnalysisReport;
}

const InsightBox = ({ insight }: { insight: string }) => (
  <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-4">
    <div className="flex items-center gap-2 text-amber-800 font-medium mb-2">
      <Lightbulb className="h-4 w-4" />
      핵심 발견
    </div>
    <p className="text-sm text-amber-900 leading-relaxed">{insight}</p>
  </div>
);

export const Dashboard = ({ report }: DashboardProps) => {
  const kpiTrends: Record<string, 'up' | 'down' | 'neutral'> = {
    avgDiscountRate: 'down',
    newContractRatio: 'up',
    avgUnitSalePrice: 'neutral',
    totalUnitSalePrice: 'up',
    totalPaymentCompleted: 'up',
    addOnServiceRevenue: 'up',
    firstMonthFreeContracts: 'neutral',
    totalDiscountLoss: 'down',
    unpaidRate: 'down',
  };

  return (
    <div className="mx-auto max-w-7xl space-y-8 p-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">{report.reportMetadata.title}</h1>
        <p className="mt-2 text-gray-500">
          생성일: {report.reportMetadata.generatedAt}
        </p>
      </div>

      {/* Executive Summary */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-500" />
            핵심 요약
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-700 leading-relaxed">{report.executiveSummary}</p>
        </CardContent>
      </Card>

      {/* Section 1: Unit Pricing Analysis */}
      <Card>
        <CardHeader className="bg-slate-50 border-b">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-slate-600" />
            {report.unitPricingAnalysis.sectionTitle}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-8">
          {/* KPI Summary */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b">
              {report.unitPricingAnalysis.kpiSummary.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {report.unitPricingAnalysis.kpiSummary.items.map((item) => (
                <KPICard key={item.key} item={item} trend={kpiTrends[item.key] || 'neutral'} />
              ))}
            </div>
          </div>

          {/* Discount Efficiency Analysis */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b">
              {report.unitPricingAnalysis.discountEfficiencyAnalysis.title}
            </h3>
            <DiscountEfficiencyChart data={report.unitPricingAnalysis.discountEfficiencyAnalysis.data} />
            <InsightBox insight={report.unitPricingAnalysis.discountEfficiencyAnalysis.insight} />
          </div>

          {/* Branch Analysis */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b">
              {report.unitPricingAnalysis.branchAnalysis.title}
            </h3>
            <BranchSalesChart data={report.unitPricingAnalysis.branchAnalysis.data} />
          </div>

          {/* Market Comparison */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b flex items-center gap-2">
              <Scale className="h-5 w-5 text-slate-500" />
              {report.unitPricingAnalysis.marketComparison.title}
            </h3>
            <MarketComparisonChart data={report.unitPricingAnalysis.marketComparison.data} />
            <InsightBox insight={report.unitPricingAnalysis.marketComparison.insight} />
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Payment Analysis */}
      <Card>
        <CardHeader className="bg-slate-50 border-b">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-slate-600" />
            {report.paymentAnalysis.sectionTitle}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-8">
          {/* Payment Summary */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b">
              {report.paymentAnalysis.paymentSummary.title}
            </h3>
            <PaymentPieChart data={report.paymentAnalysis.paymentSummary.data} />
            <InsightBox insight={report.paymentAnalysis.paymentSummary.insight} />
          </div>

          {/* Care Underperforming Branches */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b">
              {report.paymentAnalysis.careUnderperformingBranches.title}
            </h3>
            <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
              <span className="font-medium">기준: </span>
              전사 케어 가입률 {report.paymentAnalysis.careUnderperformingBranches.avgCareRate}%
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {report.paymentAnalysis.careUnderperformingBranches.columnLabels.map((label) => (
                      <th key={label} className="px-4 py-3 text-left font-medium text-gray-500">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {report.paymentAnalysis.careUnderperformingBranches.data.map((row) => (
                    <tr key={row.rank}>
                      <td className="px-4 py-3">{row.rank}</td>
                      <td className="px-4 py-3 font-medium">{row.branchName}</td>
                      <td className="px-4 py-3">{row.contractCount}</td>
                      <td className="px-4 py-3">
                        <span className={row.careRate < report.paymentAnalysis.careUnderperformingBranches.avgCareRate ? 'text-red-600 font-medium' : ''}>
                          {row.careRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Family Bottom Branches */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b">
              {report.paymentAnalysis.familyBottomBranches.title}
            </h3>
            <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
              <span className="font-medium">기준: </span>
              전사 패밀리 가입률 {report.paymentAnalysis.familyBottomBranches.avgFamilyRate}%
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {report.paymentAnalysis.familyBottomBranches.columnLabels.map((label) => (
                      <th key={label} className="px-4 py-3 text-left font-medium text-gray-500">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {report.paymentAnalysis.familyBottomBranches.data.map((row) => (
                    <tr key={row.rank}>
                      <td className="px-4 py-3">{row.rank}</td>
                      <td className="px-4 py-3 font-medium">{row.branchName}</td>
                      <td className="px-4 py-3">{row.contractCount}</td>
                      <td className="px-4 py-3">
                        <span className={row.familyRate < report.paymentAnalysis.familyBottomBranches.avgFamilyRate ? 'text-red-600 font-medium' : ''}>
                          {row.familyRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Unpaid Analysis */}
      <Card>
        <CardHeader className="bg-slate-50 border-b">
          <CardTitle className="flex items-center gap-2">
            <FileWarning className="h-5 w-5 text-slate-600" />
            {report.unpaidAnalysis.sectionTitle}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-8">
          {/* Unpaid Summary */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b">
              {report.unpaidAnalysis.summary.title}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {report.unpaidAnalysis.summary.items.map((item) => (
                <div
                  key={item.key}
                  className={`rounded-lg border p-4 ${item.highlight ? 'bg-red-50 border-red-200' : 'bg-white'}`}
                >
                  <p className="text-sm text-gray-500">{item.label}</p>
                  <p className={`text-xl font-bold ${item.highlight ? 'text-red-700' : 'text-gray-900'}`}>
                    {item.value.toLocaleString()}
                    {item.unit}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Unpaid Calculation Detail */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b">
              미납금 상세 산출 내역
            </h3>
            <UnpaidCalculationChart calculation={report.unpaidAnalysis.calculation} />
          </div>

          {/* Top Unpaid Branches */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b">
              {report.unpaidAnalysis.topUnpaidBranches.title}
            </h3>
            <UnpaidBarChart data={report.unpaidAnalysis.topUnpaidBranches.data} />
          </div>

          <InsightBox insight={report.unpaidAnalysis.insight} />
        </CardContent>
      </Card>

      {/* Section 4: Add-on Service Analysis */}
      <Card>
        <CardHeader className="bg-slate-50 border-b">
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-slate-600" />
            {report.addOnServiceAnalysis.sectionTitle}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-8">
          <ServiceMetricsChart
            careMetrics={report.addOnServiceAnalysis.careService.metrics}
            familyMetrics={report.addOnServiceAnalysis.familyService.metrics}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">케어 서비스 인사이트</h4>
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
                <p className="text-sm text-blue-900">{report.addOnServiceAnalysis.careService.insight}</p>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">패밀리 서비스 인사이트</h4>
              <div className="rounded-lg bg-green-50 border border-green-200 p-4">
                <p className="text-sm text-green-900">{report.addOnServiceAnalysis.familyService.insight}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 5: Contract Analysis */}
      <Card>
        <CardHeader className="bg-slate-50 border-b">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-slate-600" />
            {report.contractAnalysis.sectionTitle}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-8">
          <ContractChart
            metrics={report.contractAnalysis.contractStructure.metrics}
            revenueData={report.contractAnalysis.revenueContribution.data}
            avgRevenue={report.contractAnalysis.revenueContribution.avgRevenuePerContract}
          />

          {/* Top New Contract Branches */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b">
              {report.contractAnalysis.topNewContractBranches.title}
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">지점명</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">신규 계약</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">총 계약</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">신규 비율</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {report.contractAnalysis.topNewContractBranches.data.map((row) => (
                    <tr key={row.branchName}>
                      <td className="px-4 py-3 font-medium">{row.branchName}</td>
                      <td className="px-4 py-3 text-right">{row.newContracts}건</td>
                      <td className="px-4 py-3 text-right">{row.totalContracts}건</td>
                      <td className="px-4 py-3 text-right">
                        <span className={row.newRatio > 30 ? 'text-green-600 font-medium' : ''}>{row.newRatio}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <InsightBox insight={report.contractAnalysis.revenueContribution.insight} />
        </CardContent>
      </Card>

      {/* Strategy Recommendation */}
      <Card className="border-l-4 border-l-green-500">
        <CardHeader className="bg-green-50/50 border-b">
          <CardTitle className="flex items-center gap-2 text-green-800">
            {report.strategicRecommendation.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <StrategyCard recommendation={report.strategicRecommendation} />
        </CardContent>
      </Card>
    </div>
  );
};
